import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isMobileBrowser } from '../../../base/environment/utils';
import { isNkfAecEnabled, isNkfAecReady, isNkfAecSettling, isNkfAecStalled } from '../../../nkf-aec/functions';
import { openSettingsDialog } from '../../../settings/actions.web';
import SessionTimeoutClock from '../../../session-timeout/components/web/SessionTimeoutClock';
import { setDeepaiTheme } from '../../actions';
import { DEEPAI_THEMES } from '../../constants';
import { getDeepaiTheme } from '../../functions';
/**
 * Unique root class so the injected <style> stays scoped.
 */
const ROOT_CLASS = 'deepai-topbar';
const STYLE = `
.${ROOT_CLASS}{
  position:fixed; top:0; left:0; right:0; height:52px; z-index:400;
  display:flex; align-items:center; gap:14px; padding:0 16px;
  background:var(--surface); border-bottom:1px solid var(--border);
  color:var(--text); font-family:var(--font);
  backdrop-filter:blur(10px); -webkit-backdrop-filter:blur(10px);
}
.${ROOT_CLASS} .brand{
  display:inline-flex; align-items:center; gap:8px;
  font-family:var(--font-head); font-weight:700; font-size:15px; letter-spacing:.3px;
}
/* バー高 52px に対して 24px。64px の画像を縮めて出すので、拡大側の粗さは出ない。 */
.${ROOT_CLASS} .brand .mark{ width:24px; height:24px; display:block; flex:0 0 auto; }
.${ROOT_CLASS} .brand .name > span{ color:var(--accent); }

/* エコーキャンセル準備中（スマホのみ）— 下部ツールバーのバッジは小さく、
   端末によっては隠れるため、ブランド名のすぐ横にも同じ状態を出す。
   遅延が確定したら消え、セッション中は再表示しない。表示のみ。 */
.${ROOT_CLASS} .aec-preparing{
  display:inline-flex; align-items:center; gap:6px; flex:0 0 auto;
  font-size:11.5px; font-weight:600; line-height:1; white-space:nowrap;
  color:#f5a524; background:rgba(245,165,36,.14);
  border:1px solid rgba(245,165,36,.45); border-radius:999px; padding:5px 9px;
}
.${ROOT_CLASS} .aec-preparing .dot{
  width:7px; height:7px; border-radius:50%; background:#f5a524;
  box-shadow:0 0 0 0 rgba(245,165,36,.75);
  animation:dai-aec-prep 1.4s ease-out infinite; flex:0 0 auto;
}
.${ROOT_CLASS} .aec-preparing.fault{
  color:#ef4444; background:rgba(239,68,68,.14); border-color:rgba(239,68,68,.5);
}
.${ROOT_CLASS} .aec-preparing.fault .dot{ background:#ef4444; }
.${ROOT_CLASS} .aec-preparing.settling{
  color:#3b82f6; background:rgba(59,130,246,.14); border-color:rgba(59,130,246,.5);
}
.${ROOT_CLASS} .aec-preparing.settling .dot{ background:#3b82f6; }
@keyframes dai-aec-prep{
  0%{ box-shadow:0 0 0 0 rgba(245,165,36,.75); }
  70%{ box-shadow:0 0 0 6px rgba(245,165,36,0); }
  100%{ box-shadow:0 0 0 0 rgba(245,165,36,0); }
}
.${ROOT_CLASS} .status{ display:inline-flex; align-items:center; gap:7px; font-size:12px; color:var(--text-dim); }
.${ROOT_CLASS} .status .dot{ width:8px; height:8px; border-radius:50%; background:#22c55e; box-shadow:0 0 8px #22c55e; }

/* 右寄せの押し出し役。theme-trigger 自身に margin-left:auto を持たせると、
   その手前に何か（時計など）を足したとき、余白の取り合いで隙間ができる。 */
.${ROOT_CLASS} .spacer{ flex:1 1 auto; }

.${ROOT_CLASS} .theme-trigger{
  display:inline-flex; align-items:center; gap:8px;
  background:var(--surface-2); color:var(--text);
  border:1px solid var(--border); border-radius:10px;
  font-family:var(--font); font-size:12.5px; font-weight:600;
  padding:7px 12px; cursor:pointer; line-height:1; transition:border-color .16s, background .16s;
}
.${ROOT_CLASS} .theme-trigger:hover{ border-color:var(--accent); }
.${ROOT_CLASS} .theme-trigger .chev{ width:14px; height:14px; opacity:.7; transition:transform .18s; }
.${ROOT_CLASS} .theme-trigger.open .chev{ transform:rotate(180deg); }

.${ROOT_CLASS} .gear{
  margin-left:4px; display:inline-flex; align-items:center; justify-content:center;
  background:transparent; border:0; color:var(--text-dim);
  cursor:pointer; padding:8px; border-radius:10px; transition:background .16s, color .16s, transform .16s;
}
.${ROOT_CLASS} .gear svg{ width:24px; height:24px; display:block; }
.${ROOT_CLASS} .gear:hover{ background:var(--surface-2); color:var(--accent); transform:rotate(30deg); }

/* Menu is portaled to <body> so it can't be hidden by the chat panel etc.
   Unscoped class; theme tokens on :root[data-theme] apply globally. */
.deepai-theme-menu{
  position:fixed; z-index:600; min-width:186px; max-height:70vh; overflow-y:auto;
  background:var(--surface); border:1px solid var(--border); border-radius:14px;
  box-shadow:var(--shadow); padding:6px; font-family:var(--font);
  backdrop-filter:blur(16px) saturate(150%); -webkit-backdrop-filter:blur(16px) saturate(150%);
}
.deepai-theme-menu .item{
  display:flex; align-items:center; gap:9px; width:100%;
  background:transparent; border:0; color:var(--text);
  font-family:var(--font); font-size:13px; font-weight:600; text-align:left;
  padding:9px 10px; border-radius:9px; cursor:pointer; transition:background .12s;
}
.deepai-theme-menu .item:hover{ background:var(--surface-2); }
.deepai-theme-menu .item.sel{ color:var(--accent); }
.deepai-theme-menu .item .dot2{ width:9px; height:9px; border-radius:50%; background:var(--border); flex:0 0 auto; }
.deepai-theme-menu .item.sel .dot2{ background:var(--accent); box-shadow:0 0 8px var(--glow); }
`;
/**
 * Thin themed top bar (brand + status + theme picker + settings) shown when a
 * DeepAI theme is active. Part of the themed conference composition.
 *
 * @returns {JSX.Element | null}
 */
export default function DeepaiTopBar() {
    const theme = useSelector((state) => getDeepaiTheme(state));
    // スマホは下部ツールバーのバッジが見づらいので、上部にも準備状態を出す。
    // 表示のみ — 音声経路には一切影響しない。
    const aecPreparing = useSelector((state) => isNkfAecEnabled(state) && !isNkfAecReady(state));
    const aecStalled = useSelector((state) => isNkfAecEnabled(state) && isNkfAecStalled(state));
    const aecSettling = useSelector((state) => isNkfAecSettling(state));
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, right: 0 });
    const triggerRef = useRef(null);
    const reposition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) {
            return;
        }
        const r = el.getBoundingClientRect();
        setPos({
            top: r.bottom + 8,
            right: window.innerWidth - r.right
        });
    }, []);
    useLayoutEffect(() => {
        if (open) {
            reposition();
        }
    }, [open, reposition]);
    useEffect(() => {
        if (!open) {
            return;
        }
        const onDoc = (e) => {
            const t = e.target;
            if (triggerRef.current && !triggerRef.current.contains(t) && !t?.closest?.('.deepai-theme-menu')) {
                setOpen(false);
            }
        };
        const onKey = (e) => e.key === 'Escape' && setOpen(false);
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        window.addEventListener('resize', reposition);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('resize', reposition);
        };
    }, [open, reposition]);
    const onSelect = useCallback((value) => {
        dispatch(setDeepaiTheme(value));
        setOpen(false);
    }, [dispatch]);
    if (!theme) {
        return null;
    }
    const currentLabel = DEEPAI_THEMES.find(o => o.value === theme)?.label ?? theme;
    return (React.createElement("div", { className: ROOT_CLASS },
        React.createElement("style", null, STYLE),
        React.createElement("div", { className: 'brand' },
            React.createElement("img", { alt: '', className: 'mark', src: 'images/deepai-mark-64.png' }),
            React.createElement("span", { className: 'name' },
                'DeepAI ',
                React.createElement("span", null, 'Meeting'))),
        isMobileBrowser() && (aecPreparing || aecStalled) && (React.createElement("div", { className: `aec-preparing${aecStalled ? ' fault' : aecSettling ? ' settling' : ''}`, title: aecStalled
                ? 'エコーキャンセルが動作していません。音声は送信されていません。'
                : aecSettling
                    ? 'エコーの位置は分かりました。調整が終わるまで音声を送っていません。'
                    : 'エコーの位置を測っています。その間、音声は送っていません。' },
            React.createElement("span", { className: 'dot' }),
            aecStalled ? '音声 未送信（異常）'
                : aecSettling ? '音声 未送信（調整中）' : '音声 未送信（準備中）')),
        React.createElement("div", { className: 'status' },
            React.createElement("span", { className: 'dot' }),
            "\u63A5\u7D9A\u4E2D"),
        React.createElement("div", { className: 'spacer' }),
        React.createElement(SessionTimeoutClock, { inBar: true }),
        React.createElement("button", { "aria-expanded": open, "aria-haspopup": 'listbox', className: `theme-trigger ${open ? 'open' : ''}`, 
            // eslint-disable-next-line react/jsx-no-bind
            onClick: () => setOpen(o => !o), ref: triggerRef, type: 'button' },
            React.createElement("span", null, currentLabel),
            React.createElement("svg", { className: 'chev', fill: 'none', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 2, viewBox: '0 0 24 24' },
                React.createElement("path", { d: 'M6 9l6 6 6-6' }))),
        React.createElement("button", { "aria-label": '\u8A2D\u5B9A', className: 'gear', onClick: () => dispatch(openSettingsDialog(undefined, false)), type: 'button' },
            React.createElement("svg", { fill: 'none', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 1.7, viewBox: '0 0 24 24' },
                React.createElement("circle", { cx: 12, cy: 12, r: 3.2 }),
                React.createElement("path", { d: 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z' }))),
        open && createPortal(React.createElement("div", { className: 'deepai-theme-menu', role: 'listbox', style: { top: `${pos.top}px`, right: `${pos.right}px` } }, DEEPAI_THEMES.map(o => (React.createElement("button", { "aria-selected": o.value === theme, className: `item ${o.value === theme ? 'sel' : ''}`, key: o.value, 
            // eslint-disable-next-line react/jsx-no-bind
            onClick: () => onSelect(o.value), role: 'option', type: 'button' },
            React.createElement("span", { className: 'dot2' }),
            React.createElement("span", null, o.label))))), document.body)));
}
